import type { CSSProperties } from 'react'

import { cn } from '@/lib/cn'
import type { TitleSegment } from '@/lib/theme'

/**
 * Le titre de l'édition, composé comme sur l'affiche.
 *
 * Une affiche n'écrit pas son titre d'un seul trait. Celle de 2026 pose « THE
 * MASKED NIGHT » en capitales dorées, « des » en petite anglaise crème, et
 * « Suspendus » en grande anglaise rouge qui remonte sur la ligne du dessus.
 * Trois traitements pour un seul nom.
 *
 * D'où la composition **par segments**, chacun avec sa fonte, sa taille, sa
 * couleur et son décalage — le tout venant de la base, jamais du code. La
 * règle qui tenait jusqu'ici (« le dernier mot passe en cursive ») donnait le
 * bon résultat pour un nom, et un seul ; elle est tombée au premier
 * changement de nom.
 *
 * ─── Pourquoi une taille en pourcentage ───
 *
 * Chaque segment porte une échelle, pas une mesure. La base est responsive
 * (`clamp`), donc un segment à 145 % reste à 145 % de quelque chose qui tient
 * sur un téléphone. Une taille en pixels choisie devant un écran d'ordinateur
 * déborderait de tous les autres — et personne ne s'en apercevrait avant la
 * mise en ligne.
 *
 * Sans composition enregistrée, on retombe sur l'ancien découpage : une
 * édition qui n'a pas encore réglé son titre doit rester présentable.
 */
const FONTS: Record<string, string> = {
  display: 'font-display',
  script: 'font-script',
  sans: 'font-sans',
}

/** La base sur laquelle s'appliquent les échelles. Celle de `display-xl`. */
const BASE_SIZE = 'clamp(2.75rem, 9vw, 6rem)'

export function EventTitle({
  name,
  segments,
  className,
}: {
  name: string
  segments?: TitleSegment[] | undefined
  className?: string
}) {
  const composed = segments && segments.length > 0 ? segments : fallback(name)

  return (
    <h1 className={cn('font-display', className)}>
      {composed.map((segment, index) => (
        <Segment key={`${segment.text}-${index}`} segment={segment} />
      ))}
    </h1>
  )
}

function Segment({ segment }: { segment: TitleSegment }) {
  const size = `calc(${BASE_SIZE} * ${segment.scale / 100})`

  const style: CSSProperties = {
    fontSize: size,
    fontWeight: segment.weight,
    letterSpacing: `${segment.letter_spacing}em`,
    lineHeight: 1.1,
  }
  if (segment.italic) style.fontStyle = 'italic'
  if (segment.offset_y) style.marginTop = `${segment.offset_y}em`
  if (segment.offset_x) style.marginLeft = `${segment.offset_x}em`

  // Le dégradé remplace la couleur du texte, d'où le fond découpé sur le
  // glyphe et le texte rendu transparent. Une couleur unie prend le chemin
  // normal — appliquer un dégradé à tout coûterait un rendu plus lourd pour
  // rien.
  if (segment.fill === 'champagne') {
    return (
      <span
        style={style}
        className={cn(
          'block bg-gradient-to-b from-champagne-300 via-champagne-400 to-champagne-500',
          'bg-clip-text text-transparent',
          FONTS[segment.font] ?? FONTS.display,
          segment.uppercase && 'uppercase',
          !segment.new_line && 'inline-block',
        )}
      >
        {segment.text}
      </span>
    )
  }

  return (
    <span
      style={{ ...style, color: segment.color }}
      className={cn(
        'block',
        FONTS[segment.font] ?? FONTS.display,
        segment.uppercase && 'uppercase',
        !segment.new_line && 'inline-block',
      )}
    >
      {segment.text}
    </span>
  )
}

/**
 * L'ancien découpage, gardé comme filet.
 *
 * Le dernier mot passe en anglaise rouge, le reste en capitales dorées. Un nom
 * d'un seul mot ne se coupe pas : une anglaise isolée n'aurait rien à
 * accompagner.
 */
function fallback(name: string): TitleSegment[] {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return []

  const caps: TitleSegment = {
    text: (words.length > 1 ? words.slice(0, -1).join(' ') : words[0]!).toUpperCase(),
    font: 'display',
    scale: 100,
    fill: 'champagne',
    color: '#d9c08a',
    weight: 500,
    italic: false,
    uppercase: true,
    letter_spacing: 0,
    offset_y: 0,
    offset_x: 0,
    new_line: true,
  }
  if (words.length === 1) return [caps]

  const last = words[words.length - 1]!
  return [
    caps,
    {
      ...caps,
      text: last.charAt(0).toUpperCase() + last.slice(1).toLowerCase(),
      font: 'script',
      scale: 145,
      fill: 'solid',
      color: '#e85a38',
      weight: 400,
      uppercase: false,
      offset_y: -0.24,
      offset_x: 0.1,
    },
  ]
}
